import classNames from "classnames";
import { IconButton } from "../IconButton/IconButton";
import { Moveable } from "../Moveable/Moveable";
import { Portal } from "../Portal/Portal";
import { Coordinate } from "client/Misc/commonTypes";
import { setTrackingId, hasFlag } from "client/Misc/utils";
import { newGuid, Guid } from "client_legacy/ndaw/guid";
import { SizeInformation } from "client_legacy/ndaw/sizing";
import {
	CSSProperties,
	Dispatch,
	PropsWithChildren,
	ReactElement,
	RefObject,
	SetStateAction,
	useCallback,
	useLayoutEffect,
	useMemo,
	useRef,
	useState
} from "react";
import styles from "./Window.module.less";
import createVanilla, { StoreApi } from "zustand/vanilla";
import create, { UseStore } from "zustand";
import {
	DragCallbackArgs,
	useCallbackWithoutArguments,
	useDraggable
} from "client/Hooks";

export type WindowProps = {
	title: string;
	isResizable: boolean;
	trackingId: string;
	onClose: () => void;
};

export const Window = (props: PropsWithChildren<WindowProps>): ReactElement => {
	const {
		title,
		isResizable,
		trackingId,
		onClose,
		children
	} = props;

	const [wasMaximizedByAction, setWasMaximizedByAction] = useState<boolean>(true);

	const container: HTMLElement = document.body;

	const [dragMovement, setDragMovement] = useState<Coordinate | null>(null);
	const [dimensionAndPosition, setDimensionAndPosition] = useState<DimensionAndPosition>(
		(): DimensionAndPosition => {
			const dimension: SizeInformation = {
				width: 800,
				height: 600
			};

			const position: Coordinate = {
				y: (container.clientHeight - dimension.height) / 2,
				x: (container.clientWidth - dimension.width) / 2
			};

			return {
				dimension,
				position
			};
		}
	);

	const [bringToFront, order] = useWindowManagement();

	const isMaximized: boolean = !isResizable || wasMaximizedByAction;

	const dragMoveHandler = useCallback(
		(args: DragCallbackArgs): void => {
			setDragMovement(args.movement);
		},
		[]
	);

	const dragStopHandler = useCallback(
		(args: DragCallbackArgs): void => {
			setDimensionAndPosition(
				(s) => ({
					...s,
					position: {
						x: s.position.x + args.movement.x,
						y: s.position.y + args.movement.y,
					}
				})
			);

			setDragMovement(null);
		},
		[]
	);

	const { ref } = useDraggable({
		onStart: dragMoveHandler,
		onMove: dragMoveHandler,
		onStop: dragStopHandler,
		isEnabled: !isMaximized
	});

	const toggleMaximizeHandler = useCallback(
		(): void => {
			setWasMaximizedByAction(x => !x);
		},
		[]
	);


	const bringToFrontHandler: () => void = useCallbackWithoutArguments(bringToFront);

	const style: CSSProperties = isMaximized
		? {
			["--window-order"]: order
		}
		: {
			["--window-order"]: order,
			width: dimensionAndPosition.dimension
				.width,
			height: dimensionAndPosition.dimension
				.height,
			left: dimensionAndPosition.position
				.x,
			top: dimensionAndPosition.position
				.y,
			translate: dragMovement
				? `${dragMovement.x}px ${dragMovement.y}px`
				: undefined
		};

	return (
		<Portal container={document.body}>
			<div
				{...setTrackingId(trackingId)}
				className={classNames(
					styles.root,
					{
						[styles.root_maximized]: isMaximized
					}
				)}
				style={style}
				onMouseDown={bringToFrontHandler}
				onTouchStart={bringToFrontHandler}
			>
				<header className={styles.titleBar}>
					<div
						className={styles.titleWrapper}
						ref={ref}
						onDoubleClick={isResizable ? toggleMaximizeHandler : undefined}
					>
						<h4 className={styles.title} title={title}>
							{title}
						</h4>
					</div>

					<ul className={styles.actions}>
						{isResizable && (
							<li>
								<IconButton
									trackingId={isMaximized ? "normalize" : "maximize"}
									kind="transparent-colored"
									icon={{
										kind: "font",
										name: isMaximized
											? "window-restore"
											: "window-maximize",
										color: "primary-contrast"
									}}
									onClick={toggleMaximizeHandler}
									height="fill"
								/>
							</li>
						)}

						<li>
							<IconButton
								trackingId="close"
								kind="transparent-colored"
								icon={{
									kind: "font",
									name: "xmark",
									color: "primary-contrast"
								}}
								onClick={onClose}
								height="fill"
							/>
						</li>
					</ul>
				</header>

				<div className={styles.contentWrapper}>
					<div className={styles.content}>
						{children}
					</div>
				</div>

				{isResizable && !isMaximized && (
					<>
						<ResizeHandles
							dimensionAndPosition={dimensionAndPosition}
							setDimensionAndPosition={setDimensionAndPosition}
						/>
					</>
				)}
			</div>
		</Portal>
	);
};
Window.displayName = "Window";

type DimensionAndPosition = {
	position: Coordinate;
	dimension: SizeInformation;
};

type WindowManagementStoreData = {
	windowIds: readonly Guid[];
	register: (id: Guid) => void;
	deregister: (id: Guid) => void;
	bringToFront: (id: Guid) => void;
};

const windowManagementStore: StoreApi<WindowManagementStoreData> = createVanilla(
	(set): WindowManagementStoreData => {
		return {
			windowIds: [],
			register: (id: Guid) => {
				set(
					(x) => ({
						windowIds: [...x.windowIds, id]
					})
				);

				return id;
			},
			deregister: (id: Guid) => {
				set(
					(x) => ({
						windowIds: x.windowIds.filter(z => z !== id)
					})
				);
			},
			bringToFront: (id: Guid) => {
				set(
					(x) => {
						const newWindowIds: Guid[] = x.windowIds.filter(z => z !== id);
						const idExist: boolean = newWindowIds.length !== x.windowIds.length;

						if (idExist) {
							newWindowIds.push(id);
						}

						return {
							windowIds: newWindowIds
						};
					}
				);
			}
		};
	}
);

const useWindowManagementStore: UseStore<WindowManagementStoreData> = create(windowManagementStore);

function useWindowManagement(): [bringToFront: () => void, order: number] {
	const [id] = useState<Guid>(() => newGuid());

	const {
		register,
		deregister,
		bringToFront: bringToFrontOnStore,
		windowIds
	} = useWindowManagementStore();

	useLayoutEffect(
		(): (() => void) => {
			register(id);

			return (): void => deregister(id);
		},
		[
			deregister,
			id,
			register
		]
	);

	const order = useMemo(
		(): number => {
			return windowIds.indexOf(id);
		},
		[id, windowIds]
	);

	const bringToFront = useCallback(
		(): void => {
			bringToFrontOnStore(id);
		},
		[bringToFrontOnStore, id]
	);

	return [bringToFront, order];
}

const MIN_WIDTH = 100;
const MIN_HEIGHT = 100;

type ResizeHandlesProps = {
	dimensionAndPosition: DimensionAndPosition;
	setDimensionAndPosition: Dispatch<SetStateAction<DimensionAndPosition>>;
};

const ResizeHandles = (props: ResizeHandlesProps): ReactElement => {
	const {
		dimensionAndPosition,
		setDimensionAndPosition
	} = props;

	const [isResizing, setIsResizing] = useState<boolean>(false);
	const infoBeforeResizeRef: RefObject<DimensionAndPosition | null> = useRef<DimensionAndPosition>(null);

	const resizeStartHandler = useCallback(
		(): void => {
			infoBeforeResizeRef.current = dimensionAndPosition;
			setIsResizing(true);
		},
		[dimensionAndPosition]
	);

	const resizeStopHandler = useCallback(
		(): void => {
			infoBeforeResizeRef.current = null;
			setIsResizing(false);
		},
		[]
	);

	const resizeHandler = useCallback(
		(
			offsetFromStart: Coordinate,
			edge: WindowEdge
		): void => {
			const { current: infoBeforeResize } = infoBeforeResizeRef;
			if (!infoBeforeResize) {
				return;
			}

			let {
				position: {
					x,
					y
				},
				dimension: {
					width,
					height
				}
			} = infoBeforeResize;

			if (hasFlag(edge, WindowEdge.Top)) {
				height = Math.max(
					infoBeforeResize.dimension.height - offsetFromStart.y,
					MIN_HEIGHT
				);

				y = (infoBeforeResize.position.y + infoBeforeResize.dimension.height) - height;
			}

			if (hasFlag(edge, WindowEdge.Right)) {
				width = Math.max(
					infoBeforeResize.dimension.width + offsetFromStart.x,
					MIN_WIDTH
				);
			}

			if (hasFlag(edge, WindowEdge.Bottom)) {
				height = Math.max(
					infoBeforeResize.dimension.height + offsetFromStart.y,
					MIN_HEIGHT
				);
			}

			if (hasFlag(edge, WindowEdge.Left)) {
				width = Math.max(
					infoBeforeResize.dimension.width - offsetFromStart.x,
					MIN_WIDTH
				);

				x = (infoBeforeResize.position.x + infoBeforeResize.dimension.width) - width;
			}

			setDimensionAndPosition(
				{
					position: { x, y },
					dimension: { height, width }
				}
			);
		},
		[setDimensionAndPosition]
	);

	const resizeConfigurations = useMemo(
		(): readonly ResizeHandleConfiguration[] => {
			return resizeHandleEdges.map(
				(edge): ResizeHandleConfiguration => ({
					edge: edge,
					style: windowEdgeToStyleMap[edge],
					handler: (x) => resizeHandler(x, edge)
				})
			);
		},
		[resizeHandler]
	);

	return (
		<>
			{isResizing && (
				<>

					{/* This overlay is here to prevent the window content from interupting (e.g. preventDefault) the resize operation */}
					<div className={styles.resizeOverlay} />
				</>
			)}

			{resizeConfigurations.map((x) => (
				<div
					key={x.edge}
					className={classNames(
						styles.resizeHandle,
						x.style
					)}
				>

					<Moveable
						onStart={resizeStartHandler}
						onMove={x.handler}
						onEnd={resizeStopHandler}
					/>
				</div>
			))}
		</>
	);
};
ResizeHandles.displayName = "ResizeHandles";

enum WindowEdge {
	Top = 0x1 << 0,
	Right = 0x1 << 1,
	Bottom = 0x1 << 2,
	Left = 0x1 << 3
}

type ResizeHandleConfiguration = {
	edge: WindowEdge;
	style: string | undefined;
	handler: (offsetFromStart: Coordinate) => void;
};

const resizeHandleEdges: readonly WindowEdge[] = [
	WindowEdge.Top,
	WindowEdge.Top | WindowEdge.Right,
	WindowEdge.Right,
	WindowEdge.Bottom | WindowEdge.Right,
	WindowEdge.Bottom,
	WindowEdge.Bottom | WindowEdge.Left,
	WindowEdge.Left,
	WindowEdge.Top | WindowEdge.Left
];

const windowEdgeToStyleMap: Record<WindowEdge, string> = {
	[WindowEdge.Top]: styles.resizeHandle_top,
	[WindowEdge.Top | WindowEdge.Right]: styles.resizeHandle_topRight,
	[WindowEdge.Right]: styles.resizeHandle_right,
	[WindowEdge.Bottom | WindowEdge.Right]: styles.resizeHandle_bottomRight,
	[WindowEdge.Bottom]: styles.resizeHandle_bottom,
	[WindowEdge.Bottom | WindowEdge.Left]: styles.resizeHandle_bottomLeft,
	[WindowEdge.Left]: styles.resizeHandle_left,
	[WindowEdge.Top | WindowEdge.Left]: styles.resizeHandle_topLeft,
};
